import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function DemoLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const startDemo = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post("/demo/login");
      await refresh();
      setSuccess(true);
      setTimeout(() => nav("/app"), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex items-center justify-center px-5">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-6 h-6 rounded-sm bg-zinc-100 text-zinc-950 grid place-items-center">
              <Sparkles size={13} />
            </div>
            <span className="font-heading font-bold tracking-tight">MarketTrace</span>
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Try MarketTrace</h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Explore a fully populated demo environment. No account needed.
          </p>
        </div>

        <div className="rounded-lg border border-zinc-800 bg-[#0b0b0f] p-6 space-y-4">
          <div className="text-xs text-zinc-500 space-y-2">
            <p className="flex items-start gap-2">
              <CheckCircle size={13} className="text-emerald-500 mt-0.5 shrink-0" />
              <span>15 demo markets across Crypto, Economics, Politics, Sports</span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle size={13} className="text-emerald-500 mt-0.5 shrink-0" />
              <span>20 demo traders with positions and P&amp;L</span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle size={13} className="text-emerald-500 mt-0.5 shrink-0" />
              <span>Live signals with confidence scores and actionability</span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle size={13} className="text-emerald-500 mt-0.5 shrink-0" />
              <span>Power plan — all features unlocked</span>
            </p>
            <p className="flex items-start gap-2">
              <AlertCircle size={13} className="text-amber-500 mt-0.5 shrink-0" />
              <span>Data is simulated and clearly labeled as [DEMO]</span>
            </p>
          </div>

          {error && (
            <div className="text-xs text-red-400 bg-red-950/30 border border-red-800 rounded px-3 py-2">
              {error}
            </div>
          )}

          {success && (
            <div className="text-xs text-emerald-400 bg-emerald-950/30 border border-emerald-800 rounded px-3 py-2 flex items-center gap-2">
              <CheckCircle size={13} />
              Demo ready — redirecting to dashboard…
            </div>
          )}

          <button
            onClick={startDemo}
            disabled={loading || success}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : null}
            {loading ? "Setting up demo…" : "Launch Demo"}
          </button>

          <p className="text-center text-xs text-zinc-600">
            Want the real thing? <Link to="/register" className="text-zinc-400 hover:text-zinc-200">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
